import React, { useState } from 'react';
import RoleBadge, { ROLE_LABELS_PLURAL } from '../roleUtils';
import UserPicker from '../../../components/UserPicker';
import styles from './RoleSection.module.css';

const ROLE_BADGE_CLASS = {
    annotator: styles.roleBadgeAnnotator,
    reviewer: styles.roleBadgeReviewer,
    admin: styles.roleBadgeAdmin,
};

function RoleSection({
    role,
    participants,
    onAddRole,
    onRemoveRole,
    isSelfRemovalLocked,
    disableActions = false,
}) {
    const [adding, setAdding] = useState(false);

    const inRole = participants.filter((p) => p.roles?.includes(role));
    const excludeIds = inRole.map((p) => p.userId);

    function formatName(p) {
        const full = [p.firstName, p.lastName].filter(Boolean).join(' ');
        return full ? `${p.login} (${full})` : p.login;
    }

    function handleSelect(user) {
        onAddRole(String(user._id), role, user);
        setAdding(false);
    }

    return (
        <div className={styles.roleSection}>
            <div className={styles.roleSectionHeader}>
                <span className={styles.roleSectionTitle}>
                    <RoleBadge
                        role={role}
                        variant="short"
                        className={`${styles.roleBadge} ${ROLE_BADGE_CLASS[role] || ''}`}
                    />
                </span>
                {!adding && (
                    <button
                        type="button"
                        className={styles.addRoleButton}
                        onClick={() => setAdding(true)}
                    >
                        + Add
                    </button>
                )}
            </div>

            {inRole.length === 0 && !adding && (
                <p className={styles.emptyRoleState}>None assigned.</p>
            )}

            {inRole.map((p) => {
                const locked = isSelfRemovalLocked(p.userId, role);
                return (
                    <div key={p.userId} className={styles.participantRow}>
                        <span className={styles.participantName}>{formatName(p)}</span>
                        <button
                            type="button"
                            className={styles.removeButton}
                            onClick={() => !locked && !disableActions && onRemoveRole(p.userId, role)}
                            disabled={locked || disableActions}
                            title={locked
                                ? 'You cannot remove your own admin role. Ask another project admin to do this.'
                                : `Remove from ${ROLE_LABELS_PLURAL[role]}`
                            }
                        >
                            ✕
                        </button>
                    </div>
                );
            })}

            {adding && (
                <div className={styles.addParticipantRow}>
                    <UserPicker
                        onSelect={handleSelect}
                        excludeIds={excludeIds}
                        placeholder={`Search to add ${ROLE_LABELS_PLURAL[role].toLowerCase()}…`}
                        autoFocus
                        disabled={disableActions}
                    />
                    <button
                        type="button"
                        className={styles.cancelAddButton}
                        onClick={() => setAdding(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
}

export default React.memo(RoleSection);
